import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { IPill } from "@app/directives/_veri/v-pills/v-pills.component";
import { VideoEventCode } from "@app/main/components/videos/enums/VideoEventCode";
import { VideoEventRisk } from "@app/main/components/videos/enums/VideoEventRisk";
import { VideoStatus } from "@app/main/components/videos/enums/VideoStatus";
import { VideoStreamType } from "@app/main/components/videos/enums/VideoStreamType";
import { ICameraChannel, IVideoStream } from "@app/main/components/videos/models/ICameraChannel";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { CameraService } from "@app/main/components/videos/services/camera.service";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { VideoEventCodeService } from "@app/main/components/videos/services/video-event-code.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { VehicleService } from "@app/services/vehicle.service";
import * as angular from "angular";
import { CameraModule } from "../../camera.module.ajs";
import "./videoEventView.component.scss";

interface IComponentBindings {
    eventId: number;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    eventId: number;

    videoEvent: IVideoEntry;
    videoEventDuration: number;
    videoEventDisplayDate: string;

    deviceNotInstalled: boolean;
    isLiveStreamCamera: boolean;

    streamingChannels: ICameraChannel[];
    selectedChannels: IVideoStream[];
    selectedStreamType: VideoStreamType;

    selectedTab: VTabComponent;

    loading: boolean;
    requestingVideo: boolean;
    videoRequested: boolean;

    bufferedStartTime: Date;
    bufferedEndTime: Date;

    eventRisk: VideoEventRisk;
    VideoEventRisk = VideoEventRisk;
    riskPill: IPill<any>[];

    static $inject = ["$scope", "$mdDialog", "cameraService", "dateService", "navigationService", "translationService", "vehicleService", "videoEntryService", "videoEventCodeService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly cameraService: CameraService,
        private readonly dateService: DateService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly videoEntryService: VideoEntryService,
        private readonly videoEventCodeService: VideoEventCodeService
    ) {
    }

    $onInit(): void {
        this.getEvent();
    }

    onSelectedChannelsChanged($event: ICameraChannel[]): void {
        this.videoRequested = false;
        this.selectedChannels = $event;
        this.$scope.$apply();
    }

    requestVideo(): void {
        this.requestingVideo = true;

        this.cameraService.videoRestrictionCheck({
            VehicleId: this.videoEvent.vehicle.Id,
            Length: this.videoEventDuration,
            VideoStartTime: this.videoEvent.VideoStartTime,
            VideoId: this.videoEvent.Id,
            StreamType: this.selectedStreamType || VideoStreamType.Main
        }).then(async result => {

            if (!result?.CanBeRequested) {
                const alert = this.$mdDialog
                    .alert()
                    .title(this.translationService.t(`requestVideo.VideoRequestRestrictedTitle`))
                    .ok("Ok")
                    .multiple(true)
                    .textContent(this.translationService.t(`requestVideo.VideoRequestRestrictedMessage`));

                this.$mdDialog.show(alert);

                return;
            }

            this.videoEvent.Status = VideoStatus.UserUploadRequested;
            await this.videoEntryService.updateVideoEntry(this.videoEvent);

            return this.videoEntryService.requestVideoUpload({
                VehicleId: this.videoEvent.vehicle.Id,
                Length: this.videoEventDuration,
                VideoStartTime: this.videoEvent.VideoStartTime,
                Channels: this.selectedChannels.flatMap(channel => channel.ChannelId),
                VideoId: this.videoEvent.Id,
                Latitude: this.videoEvent.Latitude,
                Longitude: this.videoEvent.Longitude,
                StreamType: this.selectedStreamType || VideoStreamType.Main
            });

        }).finally(() => {
            this.requestingVideo = false;
            this.videoRequested = true;
        });
    }

    toggleArchiveEvent($event: boolean): void {
        const confirmed: boolean = $event;
        if (!confirmed) return;

        this.loading = true;

        this.videoEntryService.toggleArchiveStatus(this.videoEvent,
            this.videoEvent.Status !== VideoStatus.Archived)
            .subscribe({
                next: (updatedVideo) => {
                    this.videoEvent = updatedVideo;
                    this.$scope.$applyAsync(() => {
                        this.navigationService.go(CameraModule.Events);
                    });


                },
                error: (error) => {
                    console.error('Error toggling archive status:', error);
                    this.loading = false;
                    this.$mdDialog.show(
                        this.$mdDialog.alert()
                            .title('Error')
                            .textContent('Failed to update archive status')
                            .ok('OK')
                    );
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    onDownloadQualityChanged($event): void {
        this.selectedStreamType = $event;
    }

    onTabSelected($event: VTabComponent): void {
        this.selectedTab = $event;
        this.$scope.$applyAsync();
    }

    //--

    private getEvent(): void {
        if (!this.eventId) {
            return;
        }

        this.loading = true;
        this.videoEntryService.getVideoEntry(this.eventId).then(event => {
            this.videoEvent = event;

            this.bufferedStartTime = this.dateService.addOrSubtractTime(this.videoEvent.VideoStartTime, -5, DateUnit.Sec);
            this.bufferedEndTime = this.dateService.addOrSubtractTime(this.videoEvent.VideoEndTime, 10, DateUnit.Sec);

            this.videoEventDuration = Math.floor(this.dateService.getDiff(this.bufferedStartTime, this.bufferedEndTime, DateUnit.Sec));

            this.videoEvent.displayEventCode = this.translationService.tEnum(VideoEventCode, "VideoEventCode", this.videoEvent.EventCode);
            this.videoEventDisplayDate = this.dateService.formatDate(this.videoEvent.VideoStartTime, { dateTime: true });

            if (this.videoEvent.Status !== VideoStatus.EventOnly) {
                this.videoRequested = true;
            }

            this.getEventRisk();

            return this.getMappings();
        }).finally(() => {
            this.loading = false;
        })
    }

    private getEventRisk(): void {
        const risk = this.videoEventCodeService.getEventRisk(this.videoEvent.EventCode);

        let status: 'error' | 'warning' | 'success';

        switch (risk) {
            case VideoEventRisk.Urgent:
                status = "error";
                break;
            case VideoEventRisk.High:
                status = "warning"
                break;
        }

        this.riskPill = [];

        this.riskPill.push({
            label: this.translationService.tEnum(VideoEventRisk, "VideoEventRisk", risk) + " " + this.translationService.t("VideoEventList.Risk"),
            data: null,
            hovering: false,
            status: status
        })
    }

    private getMappings(): Promise<void> {
        const promises = [];

        promises.push(this.cameraService.getPeripherals(this.videoEvent.VehicleId));
        promises.push(this.cameraService.getPeripheralMappings(this.videoEvent.PeripheralSerial));
        promises.push(this.getPeripheralChannels());
        promises.push(this.vehicleService.getVehicle(this.videoEvent.VehicleId))

        return Promise.all(promises).then(([peripherals, mapping, channels]) => {
            const cameraDevice = peripherals.find(peripheral => peripheral.Product.IsCameraProduct);
            this.isLiveStreamCamera = cameraDevice.Product.ProductMetadata?.CameraFeatures?.VideoRequest?.OnlineOnly;

            this.deviceNotInstalled = mapping.VehicleId != this.videoEvent.VehicleId;

            const activeChannels = this.cameraService.filterDuplicateVideoStreams(channels).filter(c => c.IsActive);
            this.streamingChannels = activeChannels;
            this.selectedChannels = activeChannels;

        }).finally(() => {
            this.loading = false;
        });
    }

    private getPeripheralChannels(): Promise<ICameraChannel[]> {
        return this.cameraService.getCameraChannels({ VehicleId: this.videoEvent.VehicleId });
    }
}

export class VideoEventViewComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            eventId: "<",
        };

        this.controller = ComponentController;
        this.template = require("./videoEventView.component.html");
    }
}
